import type { OptimizedViewData } from 'app/shared/models/view-data';
import type { FlexCompactDataPoint, FlexMonitorReading } from './flex-monitor-status.models';

const COMPACT_ENTRY_PATTERN = /^(\d+):(?:\{([^}]*)\}|([^{}:;\[\]]+))(?::(?:\{([^}]*)\}|([^{}:;\[\]]+)))?$/;
const NUMBER_PATTERN = /^[+-]?(?:(?:\d+(?:\.\d*)?)|(?:\.\d+))(?:[eE][+-]?\d+)?$/;
const INTEGER_PATTERN = /^\d+$/;

export function parseLatestFlexReadings(response: OptimizedViewData | null): Map<number, FlexMonitorReading> {
    const readings = new Map<number, FlexMonitorReading>();
    if (!response?.compactData || !Array.isArray(response.entityIds)) return readings;

    const metadata = buildEntityMetadata(response);

    for (const [timestampKey, compactValue] of Object.entries(response.compactData)) {
        const timestampSeconds = Number(timestampKey);
        if (!Number.isFinite(timestampSeconds) || typeof compactValue !== 'string') continue;

        for (const point of parseCompactValue(compactValue)) {
            if (point.ignored) continue;

            const entityId = response.entityIds[point.entityIndex];
            if (!Number.isInteger(entityId)) continue;

            const current = readings.get(entityId);
            if (current && current.timestampSeconds > timestampSeconds) continue;

            const entityMetadata = metadata.get(entityId);
            readings.set(entityId, {
                entityId,
                value: point.value,
                timestampSeconds,
                quality: point.quality,
                flags: point.flags,
                unit: entityMetadata?.unit ?? null,
                precision: entityMetadata?.precision ?? null,
            });
        }
    }

    return readings;
}

export function parseCompactValue(value: string): FlexCompactDataPoint[] {
    const compactValue = value.trim();
    if (!compactValue.startsWith('[') || !compactValue.endsWith(']')) return [];

    const content = compactValue.slice(1, -1);
    if (!content) return [];

    const points: FlexCompactDataPoint[] = [];
    for (const fragment of content.split(';')) {
        const match = COMPACT_ENTRY_PATTERN.exec(fragment.trim());
        if (!match) continue;

        const hasBracedData = match[2] !== undefined;
        const dataParts = hasBracedData ? match[2].split(',') : [match[3]];
        if ((hasBracedData && dataParts.length !== 3) || (!hasBracedData && dataParts.length !== 1)) continue;

        const readingToken = dataParts[0]?.trim();
        const qualityToken = dataParts[1]?.trim();
        const flagsToken = dataParts[2]?.trim();
        if (
            !readingToken ||
            !NUMBER_PATTERN.test(readingToken) ||
            (hasBracedData && (!qualityToken || !INTEGER_PATTERN.test(qualityToken))) ||
            (hasBracedData && (!flagsToken || !INTEGER_PATTERN.test(flagsToken)))
        ) {
            continue;
        }

        const hasBracedOptions = match[4] !== undefined;
        const optionParts = hasBracedOptions ? match[4].split(',') : [];
        if (hasBracedOptions && optionParts.length !== 2) continue;

        const optionToken = hasBracedOptions ? optionParts[0]?.trim() : match[5]?.trim();
        const originalToken = hasBracedOptions ? optionParts[1]?.trim() : null;
        if (hasBracedOptions && !optionToken) continue;
        if (optionToken && optionToken !== '0' && optionToken !== '1') continue;
        if (hasBracedOptions && (!originalToken || !NUMBER_PATTERN.test(originalToken))) continue;

        const entityIndex = Number(match[1]);
        const reading = Number(readingToken);
        const quality = qualityToken ? Number(qualityToken) : 15;
        const flags = flagsToken ? Number(flagsToken) : 0;
        const ignored = optionToken === '1';
        if (
            !Number.isInteger(entityIndex) ||
            !Number.isFinite(reading) ||
            !Number.isInteger(quality) ||
            !Number.isInteger(flags)
        ) {
            continue;
        }

        points.push({ entityIndex, value: reading, quality, flags, ignored });
    }

    return points;
}

function buildEntityMetadata(
    response: OptimizedViewData,
): Map<number, { unit: string | null; precision: number | null }> {
    const result = new Map<number, { unit: string | null; precision: number | null }>();

    for (const group of response.displayGroups ?? []) {
        for (const entity of group.entities ?? []) {
            result.set(entity.id, {
                unit: group.unit ?? null,
                precision: group.precision ?? null,
            });
        }
    }

    return result;
}
